import React, { useContext, useState } from 'react';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { getFileUrl } from '@utils/urls';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import { ChevronDown, PlayFilledAlt } from '@carbon/icons-react';
import { colorSet } from '@components/Provider';
import {
  CompanyInfoContext,
  CompanyInfoContextValue
} from '../context/CompanyInfoContext';
import PartnerFilesDetailView from './DetailView/PartnerFilesDetailView';
import NewBadge from './NewBadge';
import { CompanyInfoSection } from './styled';
import { getYoutubeThumbnailByUrl } from '@utils/youtube';

const CompanyGallery: React.FunctionComponent = () => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  const [showMore, setShowMore] = useState<boolean>(false);
  const [target, setTarget] = useState<number>(0);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { galleryList, galleryEntry, showMoreButtonContainer } = useStyles();
  const context = useContext<CompanyInfoContextValue | null>(
    CompanyInfoContext
  );
  if (!context) return null;

  const { partnerInfo } = context;
  const files = [...(partnerInfo?.partnerFiles ?? [])].reverse();
  const needShowMore = files.length > 6;
  const hasFiles = files.length > 0;
  const firstPage = needShowMore && !matches ? files.slice(0, 6) : files;

  if (!hasFiles) return null;

  return (
    <>
      <PartnerFilesDetailView
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        files={files}
        target={target}
      />
      <CompanyInfoSection id="gallery">
        <div className="section-title new-column">
          <div className="new-column">
            <CustomizedTypography>사진 및 동영상</CustomizedTypography>
            <NewBadge />
          </div>
        </div>
        {!hasFiles && (
          <CustomizedTypography className="no-data">
            업데이트 예정
          </CustomizedTypography>
        )}
        {hasFiles && (
          <div className={galleryList}>
            {(!showMore ? firstPage : files).map((file, index) => {
              const thumbnail = file.youtubeURL
                ? getYoutubeThumbnailByUrl(file.youtubeURL)
                : getFileUrl(file.videoThumbnailFileId ?? file?.fileId ?? '');
              const alt = file.youtubeURL ?? file.fileName ?? '';
              const isVideo = file.youtubeURL || file.videoThumbnailFileId;

              return (
                <div
                  className={galleryEntry}
                  key={file.id}
                  onClick={() => {
                    setTarget(index);
                    setOpenDialog(true);
                  }}
                >
                  <img loading="lazy" src={thumbnail} alt={alt} />
                  {isVideo && (
                    <div className="video-icon">
                      <PlayFilledAlt size={24} color={colorSet.primary500} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {!matches && !showMore && needShowMore && (
          <div className={showMoreButtonContainer}>
            <CustomizedButton
              variant="contained"
              color="default"
              size="large"
              endIcon={<ChevronDown size={24} color={colorSet.gray900} />}
              onClick={() => setShowMore(true)}
            >
              더보기
            </CustomizedButton>
          </div>
        )}
      </CompanyInfoSection>
    </>
  );
};

export default CompanyGallery;

const useStyles = makeStyles((theme) => ({
  galleryList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    [theme.breakpoints.down('xs')]: {
      whiteSpace: ' nowrap',
      display: 'flex',
      gap: '16px',
      overflowX: 'scroll',
      padding: '16px 16px 16px 0px',
      minWidth: 'calc(100% + 16px)'
    }
  },
  showMoreButtonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginTop: 24
  },
  galleryEntry: {
    position: 'relative',
    height: '136px',
    overflow: 'hidden',
    borderRadius: '8px',
    [theme.breakpoints.down('xs')]: {
      minWidth: '256px',
      flexShrink: 0
    },
    '&:hover': {
      cursor: 'pointer'
    },
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    '& .video-icon': {
      position: 'absolute',
      width: 36,
      height: 36,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colorSet.gray000,
      borderRadius: '50%',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)'
    }
  }
}));
